import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

// Angular Material 18 has no timepicker. This is the one shared "час" input for all of Admin:
// 24h HH:mm, always directly typable, plus a quick-pick list (same role a native
// <input type="time">'s dropdown played, just consistent across browsers and themeable). The
// value in and out is a plain "HH:mm" string - a drop-in replacement wherever that was already
// the form control's value (e.g. RecordingSchedule.startTime), so nothing sent to the server
// changes.
@Component({
	selector: 'app-time-field',
	standalone: true,
	imports: [CommonModule, MatFormFieldModule, MatInputModule, MatIconModule, MatButtonModule, MatMenuModule],
	templateUrl: './time-field.component.html',
	styleUrl: './time-field.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => TimeFieldComponent),
			multi: true,
		},
	],
})
export class TimeFieldComponent implements ControlValueAccessor {
	@Input() label = '';
	// Spacing of the quick-pick list only - typing is never limited to these steps.
	@Input() stepMinutes = 15;

	value = '';
	disabled = false;

	onChange: (value: string) => void = () => {};
	onTouched: () => void = () => {};

	get quickOptions(): string[] {
		const options: string[] = [];
		for (let h = 0; h < 24; h++) {
			for (let m = 0; m < 60; m += this.stepMinutes) {
				options.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
			}
		}
		return options;
	}

	writeValue(value: string | null): void {
		this.value = value || '';
	}

	registerOnChange(fn: (value: string) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	onInput(raw: string): void {
		this.value = raw;
		// Only propagate a syntactically valid value - a half-typed string stays local until it
		// either completes or the field is abandoned (onBlur below decides what happens then).
		if (TIME_PATTERN.test(raw)) {
			this.onChange(raw);
		}
	}

	onBlur(): void {
		this.onTouched();
		const normalized = this.normalize(this.value);
		if (normalized !== null) {
			this.value = normalized;
			this.onChange(normalized);
		}
	}

	select(time: string): void {
		this.value = time;
		this.onChange(time);
		this.onTouched();
	}

	// Accepts "9:5", "9.30", "930" and similar shorthand a person might actually type, not just the
	// canonical "09:05" - returns null (leave as typed) when it genuinely can't be read as a time.
	private normalize(raw: string): string | null {
		const cleaned = raw.trim().replace('.', ':');
		if (TIME_PATTERN.test(cleaned)) {
			return cleaned;
		}
		const digitsOnly = cleaned.replace(/\D/g, '');
		let hours: number;
		let minutes: number;
		if (cleaned.includes(':')) {
			const [h, m] = cleaned.split(':');
			hours = Number(h);
			minutes = Number(m ?? '0');
		} else if (digitsOnly.length <= 2) {
			hours = Number(digitsOnly);
			minutes = 0;
		} else {
			hours = Number(digitsOnly.slice(0, digitsOnly.length - 2));
			minutes = Number(digitsOnly.slice(-2));
		}
		if (Number.isNaN(hours) || Number.isNaN(minutes) || hours > 23 || minutes > 59) {
			return null;
		}
		return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
	}
}
